import { useNavigate } from "react-router-dom";
import { Clock, User, PlayCircle, CheckCircle, BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=225&fit=crop";

export default function CourseCard({ course, enrolled, progress = 0 }) {
  const navigate = useNavigate();

  const coverImage = course.cover_image || course.thumbnail || DEFAULT_COVER;
  const totalLessons = course.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0;

  return (
    <div
      onClick={() => navigate(`/course/${course.id}`)}
      className="group bg-white border border-slate-200 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5"
    >
      {/* Cover Image */}
      <div className="relative aspect-video bg-slate-100 overflow-hidden">
        <img
          src={coverImage}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          onError={(e) => { e.target.src = DEFAULT_COVER; }}
        />
        <div className="absolute top-2 left-2">
          <Badge className="text-[9px] px-1.5 py-0 bg-white/90 text-slate-700 border-none font-normal">
            {course.category}
          </Badge>
        </div>
        {enrolled && (
          <div className="absolute top-2 right-2">
            <Badge className="text-[9px] px-1.5 py-0 bg-emerald-500 text-white border-none">
              Enrolled
            </Badge>
          </div>
        )}
        {/* Play overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <PlayCircle className="w-12 h-12 text-white opacity-0 group-hover:opacity-90 transition-opacity" />
        </div>
      </div>

      {/* Content */}
      <div className="p-3 space-y-2">
        <h3 className="text-sm font-semibold text-slate-800 line-clamp-2 leading-snug">
          {course.title}
        </h3>

        <p className="text-[11px] text-slate-400 flex items-center gap-1">
          <User className="w-3 h-3" />
          {course.instructor || "AllBaise Learning"}
        </p>

        <div className="flex items-center gap-3 text-[10px] text-slate-400">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {course.duration_hours || 0}h
          </span>
          <span className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            {totalLessons} lessons
          </span>
        </div>

        {/* Progress bar if enrolled */}
        {enrolled && (
          <div className="pt-1">
            <div className="flex items-center justify-between text-[9px] text-slate-500 mb-1">
              <span>Progress</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
